import { ENV } from '../constants.js';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router-dom';

/**
 * Shared Sentry configuration and utilities for frontend React apps
 *
 * Provides a configurable Sentry setup that can be used across all frontend repos
 * (admin_portal, buyer_portal, checkout, carthage, etc.)
 *
 * @example
 * // In main.tsx of your app:
 * import { initializeSentry } from '@ekhodealer/ekho-common/utils';
 *
 * initializeSentry({
 *   dsn: 'your-sentry-dsn',
 *   appName: 'admin_portal',
 *   environment: import.meta.env.VITE_CURR_ENV || 'development',
 * });
 */
/**
 * Runtime context storage for domain-specific tags
 * Updated via setSentryDomainContext when app state becomes available
 */
let domainContext = {};
/**
 * Stored config for blocked routes checking
 */
let storedBlockedRoutes = [];
/**
 * Check if the current route should be blocked from sending Sentry events
 */
function isBlockedRoute(path) {
    return storedBlockedRoutes.some((route) => path.startsWith(route));
}
/**
 * Default errors to ignore across all apps
 */
const DEFAULT_IGNORED_ERRORS = [
    // Browser extension errors
    /^chrome-extension:\/\//,
    /^moz-extension:\/\//,
    // Network errors that are expected user behavior
    'Network request failed',
    'Failed to fetch',
    'Load failed',
    'NetworkError',
    // User-cancelled requests
    'AbortError',
    'The operation was aborted',
    // ResizeObserver - benign browser quirk
    'ResizeObserver loop',
    // Script loading issues from ad blockers
    'Script error.',
    // Timeout errors that aren't actionable
    'timeout exceeded',
];
/**
 * Known, intentionally ignored errors discovered over time.
 * These are not actionable and are expected in normal operation.
 */
const KNOWN_NOISE_ERRORS = [
    // Stark Iframes checkout fine but throws this error
    /Third-party iframes are not allowed to request payments/i,
];
/**
 * Default URLs to deny (third-party scripts and extensions)
 */
const DEFAULT_DENY_URLS = [
    // Browser extensions
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-extension:\/\//i,
    // Third-party analytics/widgets that may error
    /googletagmanager\.com/i,
    /google-analytics\.com/i,
    /hotjar\.com/i,
    /intercom\.io/i,
];
const reactRouterBrowserTracing = (version = 'v6') => {
    if (version === 'v6') {
        return Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        });
    }
    else {
        return Sentry.reactRouterV7BrowserTracingIntegration({
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        });
    }
};
/**
 * Initialize Sentry SDK with shared configuration
 *
 * Call this function at the entry point of the application (main.tsx)
 * before React renders.
 *
 * @example
 * initializeSentry({
 *   dsn: 'https://xxx@sentry.io/xxx',
 *   appName: 'checkout',
 *   environment: import.meta.env.VITE_CURR_ENV,
 *   initialTags: {
 *     programOwnerId: window.__OEM_CONFIG__?.programOwnerId,
 *   },
 *   blockedRoutes: ['/checkout', '/account'], // Routes handled by iframes
 * });
 */
function initializeSentry(config) {
    const { dsn, appName, environment = ENV.DEVELOPMENT, initialTags = {}, blockedRoutes = [], additionalIgnoredErrors = [], productionTracesSampleRate = 0.1, productionReplaySampleRate = 0.05, productionErrorReplaySampleRate = 1.0, reactRouterVersion = 'v6', } = config;
    // Store blocked routes for later checking
    storedBlockedRoutes = blockedRoutes;
    const isProduction = environment === ENV.PRODUCTION;
    const isLocalDev = environment === ENV.LOCAL;
    Sentry.init({
        dsn,
        environment,
        // Dynamic sampling based on route and environment
        tracesSampler: (samplingContext) => {
            const transactionName = samplingContext.name ?? '';
            // Don't trace blocked routes (handled by embedded iframes)
            if (isBlockedRoute(transactionName)) {
                return 0;
            }
            // Lower in development to reduce noise, higher in production for better coverage
            return isProduction ? productionTracesSampleRate : 1.0;
        },
        // Capture 100% of errors
        sampleRate: 1.0,
        // Enable session replay for better debugging
        replaysSessionSampleRate: isProduction ? productionReplaySampleRate : 0,
        replaysOnErrorSampleRate: isProduction ? productionErrorReplaySampleRate : 0,
        // Integrations
        integrations: [
            // Browser tracing for performance monitoring
            Sentry.browserTracingIntegration(),
            // React Router integration for route-based transaction tracking
            reactRouterBrowserTracing(reactRouterVersion),
            // Session replay for debugging (only active when sample rates > 0)
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
            }),
        ],
        // Distributed tracing: propagate trace headers to these URLs
        // This enables end-to-end tracing between frontend and backend
        tracePropagationTargets: [
            'http://127.0.0.1:5000',
            'http://localhost:5000',
            'localhost',
            /^https:\/\/us-central1-.*\.cloudfunctions\.net/,
            /^https:\/\/api\.ekhodealer\.com/,
            /^https:\/\/api\.ekho\.com/,
        ],
        // Don't send errors in local development
        enabled: !isLocalDev,
        // Filter out common non-actionable errors
        ignoreErrors: [
            ...DEFAULT_IGNORED_ERRORS,
            ...KNOWN_NOISE_ERRORS,
            ...additionalIgnoredErrors,
        ],
        // Block errors from third-party scripts and browser extensions
        denyUrls: DEFAULT_DENY_URLS,
        // Enable Sentry Structured Logs (requires SDK 9.41.0+)
        enableLogs: true,
        // Set additional tags for better filtering
        initialScope: {
            tags: {
                app: appName,
                ...Object.fromEntries(Object.entries(initialTags).filter(([, v]) => v != null)),
            },
        },
        // Enrich events with runtime context before sending
        beforeSend(event) {
            // Extract pathname from URL - event.request.url is a full URL, not a pathname
            let route = window.location.pathname;
            if (event.request?.url) {
                try {
                    route = new URL(event.request.url).pathname;
                }
                catch {
                    // If URL parsing fails, fall back to window.location.pathname
                }
            }
            // Drop events for blocked routes (handled by embedded iframes)
            if (isBlockedRoute(route)) {
                return null;
            }
            // Add domain context tags if available
            Object.entries(domainContext).forEach(([key, value]) => {
                if (value) {
                    event.tags = { ...event.tags, [key]: value };
                }
            });
            // Add current route as tag for easier filtering
            event.tags = { ...event.tags, route };
            // Add extra context for debugging
            event.extra = {
                ...event.extra,
                url: window.location.href,
            };
            return event;
        },
        // Drop performance transactions for blocked routes
        beforeSendTransaction(transaction) {
            const transactionName = transaction.transaction ?? '';
            if (isBlockedRoute(transactionName)) {
                return null;
            }
            return transaction;
        },
    });
}
/**
 * Update domain context for Sentry
 *
 * Call this when domain-specific data becomes available (e.g., programOwner, user ID).
 * These values will be added as tags to all subsequent Sentry events.
 *
 * @example
 * // In your app when context becomes available:
 * setSentryDomainContext({
 *   programOwnerId: programOwner.id,
 *   domainsSvpId: domainsProvider?.id,
 * });
 */
function setSentryDomainContext(context) {
    domainContext = { ...domainContext, ...context };
    // Also set as persistent tags for the session
    Object.entries(context).forEach(([key, value]) => {
        if (value) {
            Sentry.setTag(key, value);
        }
    });
}

export { initializeSentry, setSentryDomainContext };
//# sourceMappingURL=sentry.js.map
